import { useState } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Reveal } from "../components/shared";
import { SiteLayout, PageHero, CtaBand } from "../components/site";
import { AFFILIATES } from "../content";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Profile = ({ a }) => {
  const [open, setOpen] = useState(false);
  const first = a.person.split(" ")[0];
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`affiliate-${slug(a.name)}-profile-btn`} className="mt-4 self-start inline-flex items-center gap-1.5 rounded-full border border-[#3F4A3C]/20 px-4 py-2 font-jakarta text-sm text-[#3F4A3C] hover:bg-[#E3E9E4] transition-colors">Read {first}'s profile</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto rounded-[28px] bg-[#FBF9F4] border-[#3F4A3C]/10 p-6 sm:p-10" data-testid={`affiliate-${slug(a.name)}-profile`}>
          <div className="flex items-center gap-4">
            <img src={a.photo} alt={a.person} className="w-20 h-20 rounded-full object-cover ring-2 ring-[#C59B45]/40" />
            <div>
              <DialogTitle className="font-newsreader text-3xl font-normal text-[#1F4D3A]">{a.person}</DialogTitle>
              <DialogDescription className="mt-1 font-jakarta text-xs uppercase tracking-[0.15em] text-[#8A6A2E]">{a.name}</DialogDescription>
            </div>
          </div>
          <p className="mt-5 font-jakarta text-sm font-medium text-[#3F4A3C]">{a.profile.title}</p>
          <div className="mt-4 space-y-3 font-jakarta text-sm leading-relaxed text-[#4A4F45]">{a.profile.bio.map((t) => <p key={t}>{t}</p>)}</div>
          {a.profile.lists.map((l) => (
            <div key={l.heading} className="mt-6">
              <h4 className="font-newsreader text-xl text-[#1F4D3A]">{l.heading}</h4>
              <ul className="mt-3 space-y-2">{l.items.map((it) => <li key={it} className="flex gap-2 font-jakarta text-sm text-[#3F4A3C]"><Check className="w-4 h-4 mt-0.5 shrink-0 text-[#C59B45]" />{it}</li>)}</ul>
            </div>
          ))}
          <a href={a.url} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1F4D3A] px-6 py-3 font-jakarta text-sm text-[#F6F2E9] hover:bg-[#2C6B50] transition-colors">Visit {a.name} <ArrowUpRight className="w-4 h-4" /></a>
        </DialogContent>
      </Dialog>
    </>
  );
};

const AffiliateCard = ({ a, i }) => (
  <Reveal delay={(i % 4) * 0.06} className="h-full">
    <div data-testid={`affiliate-${slug(a.name)}`}
      className="group h-full flex flex-col rounded-[28px] bg-[#FBF9F4] border border-[#3F4A3C]/10 p-6 sm:p-7 hover:border-[#5F7C8A]/40 hover:-translate-y-1 transition-[transform,border-color] duration-500">
      <a href={a.url} target="_blank" rel="noopener noreferrer" aria-label={`${a.name} website`} className={`h-28 rounded-2xl grid place-items-center px-6 ${a.dark ? "bg-[#5F7C8A]" : "bg-white"}`}>
        <img src={a.logo} alt={`${a.name} logo`} loading="lazy" className="max-h-20 max-w-full object-contain" />
      </a>
      <h3 className="mt-6 font-newsreader text-2xl text-[#3F4A3C]">{a.name}</h3>
      {a.person && (
        <div className="mt-3 flex items-center gap-3" data-testid={`affiliate-${slug(a.name)}-person`}>
          <img src={a.photo} alt={a.person} loading="lazy" className="w-12 h-12 rounded-full object-cover ring-2 ring-[#C59B45]/40" />
          <span className="font-jakarta text-sm text-[#3F4A3C]">{a.person}</span>
        </div>
      )}
      <p className="mt-2 flex-1 font-jakarta text-sm leading-relaxed text-[#4A4F45]">{a.text}</p>
      {a.profile && <Profile a={a} />}
      <a href={a.url} target="_blank" rel="noopener noreferrer" data-testid={`affiliate-${slug(a.name)}-link`} className="mt-6 self-start inline-flex items-center gap-1.5 font-jakarta text-sm text-[#3F4A3C] hover:text-[#5F7C8A] transition-colors">
        Visit website <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    </div>
  </Reveal>
);

export default function Affiliates() {
  return (
    <SiteLayout testid="affiliates-page">
      <PageHero eyebrow="Affiliates" lines={["Good care is", "a shared effort."]} sub="Trusted local partners, for support outside what we offer." />
      <section className="max-w-[1360px] mx-auto px-5 sm:px-10 pb-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="affiliates-grid">
        {AFFILIATES.map((a, i) => <AffiliateCard key={a.name} a={a} i={i} />)}
      </section>
      <p className="max-w-[1360px] mx-auto px-5 sm:px-10 font-jakarta text-xs text-[#5E6B57]">Links open the affiliate's own website. Each organisation is independent of Inner You Development.</p>
      <CtaBand />
    </SiteLayout>
  );
}
